import { computeExposure } from "./exposure.js";
import { recuperablesParSemaine } from "./timeCost.js";
/**
 * Le moment où la personne s'engage à agir — une liste fermée.
 *
 * Un engagement de la forme « quand …, alors … » est tenu bien plus souvent
 * qu'un simple objectif (intentions d'implémentation, Gollwitzer et Sheeran,
 * 2006). La liste est fermée pour deux raisons : un texte libre serait un texte
 * de la personne, conservé trois mois, et la règle n° 4 ne le permet pas ; et
 * un déclencheur vague — « quand j'aurai le temps » — est exactement celui qui
 * ne déclenche rien.
 */
export const MOMENTS_ENGAGEMENT = [
    "chaque_lundi_matin",
    "chaque_vendredi_apres_midi",
    "chaque_fin_de_mois",
    "a_chaque_fois_que_la_tache_arrive",
];
/** Le libellé affiché d'un moment, dans la phrase « Quand …, je fais : … ». */
export const LIBELLES_MOMENT = {
    chaque_lundi_matin: "chaque lundi matin",
    chaque_vendredi_apres_midi: "chaque vendredi après-midi",
    chaque_fin_de_mois: "chaque fin de mois",
    a_chaque_fois_que_la_tache_arrive: "à chaque fois que la tâche arrive",
};
export function estMomentEngagement(valeur) {
    return typeof valeur === "string" && MOMENTS_ENGAGEMENT.includes(valeur);
}
/** On s'engage sur une à trois actions : au-delà, on ne s'engage sur rien. */
export const ENGAGEMENTS_MAX = 3;
/**
 * Une occurrence se déclare en minutes, bornées à une journée de travail.
 *
 * « La dernière fois que vous avez fait cette tâche, combien de temps ? » est
 * ancré sur un souvenir précis ; « combien d'heures par semaine » est une
 * impression, et on la surestime. Le chiffre reste déclaré, mais il se
 * compare mieux.
 */
export const MINUTES_OCCURRENCE_MAX = 600;
export function minutesValides(valeur) {
    const n = Number(valeur);
    if (valeur === null || valeur === undefined || valeur === "" || !Number.isFinite(n))
        return null;
    if (n <= 0 || n > MINUTES_OCCURRENCE_MAX)
        return null;
    return Math.round(n);
}
const arrondi = (n) => Math.round(n * 10) / 10;
/** Deux libellés désignent-ils la même tâche ? Casse et espaces ne comptent pas. */
function cle(texte) {
    return texte.normalize("NFC").trim().replace(/\s+/g, " ").toLowerCase();
}
/**
 * Compare la mesure d'avant et celle d'après.
 *
 * L'indice d'après est **recalculé** sur les tâches déclarées d'après, avec le
 * groupe d'avant : c'est le même barème appliqué deux fois, ce qui rend
 * l'écart lisible. Les tâches sont appariées par libellé ; seules les tâches
 * présentes des deux côtés entrent dans les écarts.
 */
export function comparerProgression(avant, apres) {
    if (avant.taches.length === 0 || apres.taches.length === 0) {
        throw new Error("comparerProgression : il faut des tâches des deux côtés");
    }
    const scoreAvant = computeExposure({ groupe: avant.groupe, taches: [...avant.taches] }).score;
    const scoreApres = computeExposure({ groupe: avant.groupe, taches: [...apres.taches] }).score;
    const parCle = new Map(apres.taches.map((t) => [cle(t.texte), t]));
    const communes = avant.taches
        .map((t) => ({ avant: t, apres: parCle.get(cle(t.texte)) }))
        .filter((p) => p.apres !== undefined);
    const taches = communes.map(({ avant: a, apres: b }) => ({
        texte: a.texte,
        heuresAvant: arrondi(a.heures),
        heuresApres: arrondi(b.heures),
        heuresRendues: arrondi(a.heures - b.heures),
    }));
    const heuresAvant = arrondi(communes.reduce((s, p) => s + p.avant.heures, 0));
    const heuresApres = arrondi(communes.reduce((s, p) => s + p.apres.heures, 0));
    const engagements = avant.engagements.map((e, i) => {
        const minutesApres = minutesValides(apres.minutesEngagements[i]);
        return {
            titre: e.titre,
            tache: e.tache,
            moment: e.moment,
            minutesAvant: e.minutesAvant,
            minutesApres,
            minutesRendues: e.minutesAvant !== null && minutesApres !== null ? e.minutesAvant - minutesApres : null,
        };
    });
    return {
        scoreAvant,
        scoreApres,
        ecartScore: scoreApres - scoreAvant,
        heuresAvant,
        heuresApres,
        heuresRendues: arrondi(heuresAvant - heuresApres),
        recuperablesAvant: recuperablesParSemaine(avant.taches),
        recuperablesApres: recuperablesParSemaine(apres.taches),
        taches,
        engagements,
    };
}
/**
 * La frontière entre le gratuit et le payant.
 *
 * Le second diagnostic est gratuit, c'est tranché. S'il livrait aussi le
 * détail, il rendrait le rapport de progression inutile. Il montre donc
 * **que** quelque chose a bougé — un chiffre et son écart —, et le rapport dit
 * **où**, **combien** et **quoi faire ensuite**. Un test garde cette liste de
 * champs fermée.
 */
export function apercuGratuit(c) {
    return { scoreAvant: c.scoreAvant, scoreApres: c.scoreApres, ecartScore: c.ecartScore };
}
/**
 * Ce qui a marché chez les autres — 28/09/2026.
 *
 * Chaque rapport de progression livré laisse, anonymement, les heures rendues
 * tâche par tâche. Rassemblées par métier, elles disent ce qu'aucun assistant
 * généraliste ni aucun concurrent parti de zéro ne peut dire : quelles tâches
 * ont réellement rendu du temps à des gens du même métier.
 *
 * Trois gardes, parce que ce chiffre circulera dans des rapports payés :
 *
 * - **un seuil** : en dessous de trente mesures sur une tâche, rien n'est
 *   affiché. Trois personnes enthousiastes ne font pas une tendance, et un
 *   chiffre publié sur trop peu de monde est un faux signal au sens de la
 *   règle n° 6 ;
 * - **la médiane et l'écart interquartile**, jamais la moyenne : une personne
 *   qui déclare vingt heures rendues ne doit pas tirer tout le métier ;
 * - **le mot « déclarées »**, partout où le chiffre est imprimé.
 */
export const SEUIL_GAINS_OBSERVES = 30;
/** Quantile par interpolation linéaire, sur une liste triée. */
function quantile(tries, p) {
    const position = (tries.length - 1) * p;
    const bas = Math.floor(position);
    const haut = Math.ceil(position);
    return tries[bas] + (tries[haut] - tries[bas]) * (position - bas);
}
/** Médiane et quartiles d'une série de gains déclarés, ou `null` sous le seuil. */
export function resumerGains(valeurs, seuil = SEUIL_GAINS_OBSERVES) {
    const propres = valeurs.filter((v) => Number.isFinite(v)).slice().sort((a, b) => a - b);
    if (propres.length < seuil)
        return null;
    return {
        n: propres.length,
        mediane: arrondi(quantile(propres, 0.5)),
        q1: arrondi(quantile(propres, 0.25)),
        q3: arrondi(quantile(propres, 0.75)),
    };
}
